import { Group, InputDecimal } from '@/components/_common';
import {
  Inventory,
  InventoryFormValues,
  InventoryItemListResponse,
  InventoryItemsResponse,
  InventoryStatus,
  LaunchItemsRequest,
  ListItemInventoryParams,
  ListItemInventoryResponse,
  ProductType,
  ProductTypeLabels,
} from '@/core/domain/inventory/inventories/Inventory.types';
import { quantityFormatter } from '@/utils/number';
import {
  Alert,
  Checkbox,
  Divider,
  Form,
  FormInstance,
  Space,
  Spin,
  Tooltip,
  Typography,
  Table,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { getTableScrollX } from '@/utils/table';
import { useIntl } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { Check, X, Edit, CheckSquare } from 'react-feather';
import {
  useCreateItemInventory,
  useUpdateItemInventory,
} from '@/core/domain/inventory/inventories/inventory.hooks';
import InventoryAddItemModal from '../InventoryAddItemModal';
import { UseQueryResult } from 'react-query';
import ImportAllItemsInventoryModal from '../ImportAllItemsInventoryModal';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ResetUnmarkedItemsInventoryModal from '../ResetUnmarkedItemsInventoryModal';

type Props = {
  form: FormInstance<InventoryFormValues>;
  inventory: Inventory;
  filter?: ListItemInventoryParams;
  setFilter: React.Dispatch<React.SetStateAction<ListItemInventoryParams>>;
  inventoryItemsQueryResult: UseQueryResult<InventoryItemListResponse, unknown>;
};

const InventoryItemsList: React.FC<Props> = ({
  form,
  inventory,
  filter,
  setFilter,
  inventoryItemsQueryResult,
}) => {
  const intl = useIntl();
  const disable =
    inventory.status === InventoryStatus.DONE ||
    inventory.status === InventoryStatus.PROCESSING;

  const itemsWatch = Form.useWatch<InventoryItemsResponse[]>('items', form);

  const {
    data: inventoryItems,
    isFetching,
    dataUpdatedAt,
  } = inventoryItemsQueryResult;

  const [enableFields, setEnableFields] = useState<number | null>(null);
  const [loadingIcon, setLoadingIcon] = useState<number | null>(null);

  const createItemMutation = useCreateItemInventory(inventory.id, filter);
  const updateItemMutation = useUpdateItemInventory(inventory.id, filter);

  const handleLaunchItems = async (
    item: ListItemInventoryResponse,
    index: number,
    isChecked: boolean,
  ) => {
    try {
      setLoadingIcon(item.stockItemsDTO.partId);
      const fieldQuantity = form.getFieldValue([
        'items',
        index,
        'countedQuantity',
      ]);

      const fieldPrice = form.getFieldValue(['items', index, 'price']);

      const obj: LaunchItemsRequest = {
        partId: item.stockItemsDTO.partId ?? undefined,
        consumableId: item.stockItemsDTO.consumableId ?? undefined,
        type: item.stockItemsDTO.partId
          ? ProductType.PART
          : ProductType.CONSUMABLE,
        countedQuantity: fieldQuantity,
        checked: isChecked,
        price: fieldPrice || 0,
      };

      if (item.item?.id) {
        await updateItemMutation.mutateAsync({
          ...obj,
          id: item.item?.id,
        });
      } else {
        await createItemMutation.mutateAsync(obj);
      }
    } catch {}
    setEnableFields(null);
    setLoadingIcon(null);
  };

  useEffect(() => {
    if (inventoryItems?.data) {
      form.setFieldValue(
        'items',
        inventoryItems.data.map((item) => ({
          countedQuantity: item.item?.countedQuantity,
          price: item.item?.price || 0,
        })),
      );
    }
  }, [dataUpdatedAt]);

  const columns: ColumnsType<ListItemInventoryResponse> = [
    {
      title: <CheckSquare size={16} />,
      width: 25,
      align: 'center',
      render: (row: ListItemInventoryResponse, _, index: number) => (
        <Checkbox
          disabled={
            updateItemMutation.isLoading || createItemMutation.isLoading
          }
          checked={!!row.item?.checked}
          onChange={({ target: { checked } }) =>
            handleLaunchItems(row, index, checked)
          }
        />
      ),
    },
    {
      key: 'name',
      dataIndex: ['stockItemsDTO', 'description'],
      title: intl.formatMessage({
        id: `inventory.page.form.modal.fetchItems.table.description`,
      }),
      ellipsis: true,
      width: 130,
    },
    {
      key: 'manufacturingCode',
      dataIndex: ['stockItemsDTO', 'manufacturingCode'],
      title: intl.formatMessage({
        id: 'inventory.page.form.modal.fetchItems.table.codeManufactures',
      }),
      ellipsis: true,
      width: 130,
    },
    {
      key: 'barCode',
      dataIndex: ['stockItemsDTO', 'barcode'],
      title: intl.formatMessage({
        id: 'inventory.page.form.modal.fetchItems.table.barCode',
      }),
      ellipsis: true,
      width: 130,
    },
    {
      key: 'sku',
      dataIndex: ['stockItemsDTO', 'sku'],
      title: intl.formatMessage({
        id: 'inventory.page.form.modal.fetchItems.table.sku',
      }),
      ellipsis: true,
      width: 100,
    },
    {
      key: 'companyCode',
      dataIndex: ['stockItemsDTO', 'companyCode'],
      title: intl.formatMessage({
        id: 'inventory.page.form.modal.fetchItems.table.companyCode',
      }),
      ellipsis: true,
      width: 120,
    },
    {
      key: 'stock',
      dataIndex: ['stockItemsDTO', 'stock', `${[0]}`, 'stock'],
      title: intl.formatMessage({
        id: `inventory.page.form.modal.fetchItems.table.currentInventory`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'quantityCounted',
      title: intl.formatMessage({
        id: `inventory.page.form.modal.fetchItems.table.qtyCounted`,
      }),
      width: 100,
      render(_value, row, index: number) {
        const countedQuantity =
          (itemsWatch && itemsWatch[index]?.countedQuantity) || 0;
        const reservedQuantity = row.stockItemsDTO.stock[0]?.reserved;
        const errorMessage = `Não pode ser menor que a quantidade reservada: ${reservedQuantity}`;
        const InsufficientQuantity = countedQuantity < reservedQuantity;

        return (
          <Tooltip
            title={!disable && InsufficientQuantity ? errorMessage : ''}
            color="#ff4d4fb3"
          >
            <Form.Item name={[index, 'countedQuantity']} style={{ margin: 0 }}>
              <InputDecimal
                min={0}
                fractions={0}
                disabled={
                  (row.stockItemsDTO.partId ??
                    row.stockItemsDTO.consumableId) !== enableFields
                }
                style={{
                  borderColor:
                    !disable && InsufficientQuantity ? '#ff4d4f' : undefined,
                }}
              />
            </Form.Item>
          </Tooltip>
        );
      },
    },
    {
      key: 'price',
      title: intl.formatMessage({
        id: `inventory.page.form.modal.fetchItems.table.vlUnitary`,
      }),
      ellipsis: true,
      width: 100,
      render: (_value, row, index: number) => (
        <Form.Item noStyle name={[index, 'price']}>
          <InputDecimal
            min={0}
            disabled={
              (row.stockItemsDTO.partId ?? row.stockItemsDTO.consumableId) !==
              enableFields
            }
          />
        </Form.Item>
      ),
    },
    {
      key: 'amount',
      dataIndex: 'amount',
      title: intl.formatMessage({
        id: `inventory.page.form.modal.fetchItems.table.vlAmount`,
      }),
      ellipsis: true,

      render(_value, _row, index: number) {
        return (
          <Form.Item noStyle shouldUpdate>
            {() => {
              const qtd = form.getFieldValue([
                'items',
                index,
                'countedQuantity',
              ]);

              const price = form.getFieldValue(['items', index, 'price']);

              const sum = (qtd ?? 0) * (price ?? 0) || 0;

              return quantityFormatter(sum);
            }}
          </Form.Item>
        );
      },
      width: 110,
      align: 'center',
    },
    {
      key: 'launch',
      title: 'Ação',
      render(_value, row, index: number) {
        const disable =
          inventory.status === InventoryStatus.DONE ||
          inventory.status === InventoryStatus.PROCESSING ||
          inventory?.status === InventoryStatus.IMPORTING ||
          !!enableFields;

        const reservedQuantity = row.stockItemsDTO?.stock[0]?.reserved;
        const disableCheck =
          ((itemsWatch && itemsWatch[index]?.countedQuantity) || 0) <
          reservedQuantity;

        return (
          <>
            {(row.stockItemsDTO.partId ?? row.stockItemsDTO.consumableId) ===
            loadingIcon ? (
              <Spin size="small" />
            ) : (
              <>
                {(row.stockItemsDTO.partId ??
                  row.stockItemsDTO.consumableId) !== enableFields ? (
                  <Edit
                    style={disable ? { cursor: 'not-allowed' } : {}}
                    cursor="pointer"
                    size={23}
                    onClick={
                      disable
                        ? undefined
                        : () =>
                            setEnableFields(
                              row.stockItemsDTO.partId ??
                                row.stockItemsDTO.consumableId,
                            )
                    }
                  />
                ) : (
                  <Space>
                    <Check
                      size={23}
                      style={
                        disableCheck
                          ? { cursor: 'not-allowed' }
                          : { color: 'green' }
                      }
                      cursor="pointer"
                      onClick={() =>
                        disableCheck
                          ? undefined
                          : handleLaunchItems(row, index, true)
                      }
                    />

                    <Divider type="vertical" />

                    <X
                      size={23}
                      style={{ color: 'red' }}
                      cursor={'pointer'}
                      onClick={() => {
                        form.setFieldValue(
                          ['items', index, 'countedQuantity'],
                          row.item?.id ? row.item.countedQuantity : 0,
                        );

                        form.setFieldValue(
                          ['items', index, 'price'],
                          row.item?.price || 0,
                        );

                        setEnableFields(null);
                      }}
                    />
                  </Space>
                )}
              </>
            )}
          </>
        );
      },
      align: 'center',
      fixed: 'right',
      width: 100,
    },
  ];

  return (
    <>
      <Group position="between" style={{ marginBottom: 24 }}>
        <Typography.Title level={5} style={{ whiteSpace: 'nowrap' }}>
          {ProductTypeLabels[inventory.productType].pascalcase} do inventário:
        </Typography.Title>

        <SpaceForButton justifyContent="end">
          <ResetUnmarkedItemsInventoryModal
            inventory={inventory}
            disabled={(inventoryItems?.data || []).length < 1}
          />

          <ImportAllItemsInventoryModal inventory={inventory} />

          <InventoryAddItemModal
            inventory={inventory}
            inventoryPageResult={inventoryItems}
          />
        </SpaceForButton>
      </Group>

      {!disable &&
        !!itemsWatch &&
        itemsWatch?.some(
          (item, index) =>
            inventoryItems?.data &&
            item.countedQuantity <
              inventoryItems?.data[index]?.stockItemsDTO.stock[0]?.reserved,
        ) && (
          <Alert
            showIcon
            type="error"
            message="Existe itens que a quantidade contada é inferior à quantidade reservada!"
            style={{ margin: '24px 0' }}
          />
        )}

      <Form.List name="items">
        {() => (
          <Table
            onChange={(pagination, _f, _s) => {
              const nextToken =
                ((pagination.current || 0) - 1) * (pagination.pageSize || 0);

              setFilter((prev) => {
                return {
                  ...prev,
                  rows: pagination.pageSize,
                  nextToken: nextToken ? String(nextToken) : undefined,
                };
              });
            }}
            size="middle"
            rowKey={(row) => row.stockItemsDTO.partId}
            pagination={{
              defaultPageSize: 10,
              current: inventoryItems?.currentPage || 1,
              total: inventoryItems?.itemsCount,
            }}
            columns={columns}
            scroll={{ x: getTableScrollX(columns) }}
            loading={isFetching}
            dataSource={inventoryItems?.data}
          />
        )}
      </Form.List>
    </>
  );
};

export default InventoryItemsList;
